// File: src/pages/CustomerDashboard.jsx
// Clean design, uses Shopify API data, matches other page KPI format

import React, { useContext, useState, useMemo } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Banner,
  SkeletonPage,
  SkeletonBodyText,
  List,
  Collapsible,
  Button,
} from '@shopify/polaris';
import {
  ArrowLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@shopify/polaris-icons';
import useData from '../hooks/useData';
import { AuthContext } from '../context/AuthContext';
import LayoutWrapper from '../components/Layout/Layout';
import { cleanText } from '../utils/cleanText';
import PrescriptionBanner from '../components/Prescriptions/PrescriptionBanner';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

// Formatting helpers
const fmtMoney = (v) => v != null ? `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00';
const fmtInt = (v) => v != null ? Number(v).toLocaleString() : '0';
const fmtPct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '0.0%';

// Page Goal component
function PageGoal({ goal }) {
  return (
    <Text as="p" tone="subdued">
      <Text as="span" fontWeight="bold">{cleanText("Page Goal:")}</Text> {cleanText(goal)}
    </Text>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function CustomerDashboard() {
  const { token } = useContext(AuthContext);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const authHeaders = useMemo(
    () => (token && token !== 'mock-token' ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const commonOpts = useMemo(
    () => ({ headers: { 'Content-Type': 'application/json', ...authHeaders }, credentials: 'include' }),
    [authHeaders]
  );

  // Get sites
  const { data: sites = [], loading: sitesLoading, error: sitesError } = useData(
    token ? `sites:${token}` : 'noop',
    () => fetch(`${API}/api/sites`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    { ttlMs: 24 * 60 * 60 * 1000, deps: [token] }
  );

  const siteId = useMemo(() => {
    const ss = sessionStorage.getItem('site_id');
    if (ss) return String(ss);
    const first = Array.isArray(sites) && sites[0]?.id ? String(sites[0].id) : null;
    if (first) sessionStorage.setItem('site_id', first);
    return first;
  }, [sites]);

  // ===== MAIN DATA: Customer Intelligence Dashboard =====
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError } = useData(
    `customer-intel:dashboard:${siteId}`,
    async () => {
      if (!siteId) return null;
      const res = await fetch(
        `${API}/api/customer-intelligence/dashboard?site_id=${siteId}&days=180`,
        commonOpts
      );
      if (!res.ok) throw new Error(`Failed to fetch dashboard: ${res.status}`);
      return res.json();
    },
    { ttlMs: 60 * 60 * 1000, deps: [siteId, token] }
  );

  // ===== AI Insights =====
  const { data: ciInsights, loading: insightsLoading } = useData(
    `customer-intel:insights:${siteId}`,
    async () => {
      if (!siteId) return null;
      const res = await fetch(
        `${API}/api/customer-intelligence/ai-insights?site_id=${siteId}&days=30`,
        commonOpts
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data.summary || '';
    },
    { ttlMs: 24 * 60 * 60 * 1000, deps: [siteId, token] }
  );

  // ===== DERIVED VALUES =====
  const kpis = useMemo(() => dashboardData?.kpis || {}, [dashboardData]);
  const valueDistribution = dashboardData?.value_distribution || [];
  const frequencyDistribution = dashboardData?.frequency_distribution || [];
  const dataSource = dashboardData?.data_source || 'none';

  // Calculate headline opportunity
  const headlineOpportunity = useMemo(() => {
    const repeatRate = kpis.repeat_purchase_rate_pct || 0;
    const totalCustomers = kpis.total_customers || 0;
    const oneTimeBuyers = kpis.one_time_buyers || (totalCustomers - (kpis.repeat_customers || 0));
    
    let severity = 'success';
    let message = 'Your returning customer rate is healthy!';
    
    if (repeatRate < 20) {
      severity = 'critical';
      message = `Only ${fmtPct(repeatRate)} of customers come back for a second purchase`;
    } else if (repeatRate < 30) {
      severity = 'warning';
      message = `${fmtPct(repeatRate)} returning rate - there's room to improve`;
    }
    
    return {
      severity,
      message,
      repeatRate,
      oneTimeBuyers,
      benchmark: '27-30%'
    };
  }, [kpis]);

  // Calculate retention funnel
  const retentionFunnel = useMemo(() => {
    const total = kpis.total_customers || 0;
    const repeat = kpis.repeat_customers || 0;
    const loyal = kpis.loyal_customers || 0;
    const highValue = kpis.high_value_customers || 0;
    
    return [
      { label: 'Total Customers', count: total, pct: 100 },
      { label: 'Returned (2+ orders)', count: repeat, pct: total > 0 ? (repeat / total * 100) : 0 },
      { label: 'Loyal (4+ orders)', count: loyal, pct: total > 0 ? (loyal / total * 100) : 0 },
      { label: 'High Value ($500+)', count: highValue, pct: total > 0 ? (highValue / total * 100) : 0 },
    ];
  }, [kpis]);

  // Parse AI insights
  const parsedInsights = useMemo(() => {
    if (!ciInsights) return null;
    
    const result = {
      whatWeFound: [],
      whyItMatters: '',
      recommendations: []
    };
    
    // Helper to strip markdown formatting
    const stripMarkdown = (str) => str
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // **bold** -> bold
      .replace(/\*([^*]+)\*/g, '$1')       // *italic* -> italic
      .replace(/\*/g, '')                   // any remaining asterisks
      .replace(/_([^_]+)_/g, '$1')         // _italic_ -> italic
      .replace(/`([^`]+)`/g, '$1')         // `code` -> code
      .trim();
    
    const text = ciInsights;
    
    // Extract "What We Found" bullets
    const foundMatch = text.match(/What We Found[:\s]*([\s\S]*?)(?=(?:Why It Matters|Treatment Plan|Recommendations|\n\n))/i);
    if (foundMatch) {
      const lines = foundMatch[1].split('\n').filter(l => l.trim().startsWith('-') || l.trim().startsWith('•') || l.trim().startsWith('*'));
      result.whatWeFound = lines.map(l => stripMarkdown(l.replace(/^[-•*]\s*/, ''))).filter(Boolean);
    }
    
    // Extract "Why It Matters"
    const mattersMatch = text.match(/Why It Matters[:\s]*([\s\S]*?)(?=(?:Treatment Plan|Recommendations|\n\n|$))/i);
    if (mattersMatch) {
      result.whyItMatters = stripMarkdown(mattersMatch[1]);
    }
    
    // Extract recommendations
    const planMatch = text.match(/(?:Treatment Plan|Recommendations)[:\s]*([\s\S]*?)(?=\n\n|$)/i);
    if (planMatch) {
      const lines = planMatch[1].split('\n').filter(l => l.trim());
      result.recommendations = lines.map(l => 
        stripMarkdown(
          l.replace(/^\d+\.\s*/, '')
           .replace(/^[-•*]\s*/, '')
           .replace(/Week \d+[^:]*:?\s*/gi, '')
        )
      ).filter(Boolean);
    }
    
    return result;
  }, [ciInsights]);

  // Loading states
  const loading = sitesLoading || dashboardLoading;
  const error = sitesError || dashboardError;

  // ===== RENDER =====
  
  if (!token) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Customer Dashboard")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">{cleanText("Authentication Required")}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  if (loading || !siteId) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Customer Dashboard")}>
          <SkeletonPage>
            <SkeletonBodyText lines={10} />
          </SkeletonPage>
        </Page>
      </LayoutWrapper>
    );
  }

  if (error) {
    return (
      <LayoutWrapper>
        <Page title={cleanText("Customer Dashboard")}>
          <Layout>
            <Layout.Section>
              <Banner status="critical">{cleanText(`Error: ${error.message || error}`)}</Banner>
            </Layout.Section>
          </Layout>
        </Page>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Page
        title={cleanText("Customer Dashboard")}
        backAction={{
          content: cleanText('Back'),
          onAction: () => window.history.back(),
          icon: ArrowLeftIcon,
        }}
      >
        {/* Page Goal */}
        <PageGoal goal="Understand customer value, behavior patterns, and opportunities to increase retention and lifetime value." />

        {/* Gradient divider */}
        <div style={{
          height: 4,
          width: '100%',
          borderRadius: 4,
          margin: '8px 0 16px',
          background: 'linear-gradient(90deg, #00a8a9, #0891b2)'
        }} />

        {/* Prescription Banner */}
        <PrescriptionBanner siteId={siteId} token={token} category="customer_retention" />

        {/* Data source indicator */}
        {dataSource === 'shopify_api' && (
          <div style={{ marginBottom: 16 }}>
            <Text variant="bodySm" tone="subdued">
              {cleanText("✓ Data from Shopify (live)")}
            </Text>
          </div>
        )}

        <Layout>
          {/* ===== SECTION 1: KEY METRICS (matching PerformanceDashboard format) ===== */}
          <Layout.Section>
            <Card title={cleanText("Key Customer Metrics")} sectioned>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                
                {/* Customer Lifetime Value */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(0, 168, 169, 0.08)', border: '1px solid rgba(0, 168, 169, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00A8A9', marginBottom: 4 }}>
                    {fmtMoney(kpis.avg_customer_value || kpis.avg_order_value || 0)}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Customer Lifetime Value")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText("Avg revenue per customer")}</Text>
                </div>

                {/* Returning Customer Rate */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(0, 168, 169, 0.08)', border: '1px solid rgba(0, 168, 169, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00A8A9', marginBottom: 4 }}>
                    {fmtPct(kpis.repeat_purchase_rate_pct || 0)}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Returning Customer Rate")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText("Customers with 2+ orders")}</Text>
                </div>

                {/* Retention Rate */}
                <div style={{ padding: 20, borderRadius: 12, background: 'rgba(0, 168, 169, 0.08)', border: '1px solid rgba(0, 168, 169, 0.25)' }}>
                  <div style={{ fontSize: 28, fontWeight: 'bold', color: '#00A8A9', marginBottom: 4 }}>
                    {fmtPct(kpis.retention_rate_pct || 0)}
                  </div>
                  <Text variant="bodyMd" as="h3">{cleanText("Retention Rate (30d)")}</Text>
                  <Text variant="bodySm" tone="subdued">{cleanText("Came back this month")}</Text>
                </div>
              </div>
            </Card>
          </Layout.Section>

          {/* ===== SECTION 2: HEADLINE OPPORTUNITY ===== */}
          <Layout.Section>
            <Card 
              padding="500"
              background={
                headlineOpportunity.severity === 'critical' ? 'bg-surface-critical-subdued' :
                headlineOpportunity.severity === 'warning' ? 'bg-surface-warning-subdued' :
                'bg-surface-success-subdued'
              }
            >
              <div style={{ 
                padding: '8px 0',
                borderLeft: `4px solid ${
                  headlineOpportunity.severity === 'critical' ? '#dc2626' :
                  headlineOpportunity.severity === 'warning' ? '#f59e0b' :
                  '#10b981'
                }`,
                paddingLeft: 16
              }}>
                <Text variant="headingLg" as="h2" fontWeight="bold">
                  {cleanText("Your Retention Opportunity")}
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Text variant="bodyLg">{cleanText(headlineOpportunity.message)}</Text>
                </div>
                {headlineOpportunity.severity !== 'success' && (
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                    <div>
                      <Text variant="bodySm" tone="subdued">{cleanText("One-time buyers")}</Text>
                      <Text variant="headingMd" fontWeight="bold">{cleanText(`${fmtInt(headlineOpportunity.oneTimeBuyers)} customers`)}</Text>
                    </div>
                    <div>
                      <Text variant="bodySm" tone="subdued">{cleanText("Industry benchmark")}</Text>
                      <Text variant="headingMd" fontWeight="bold">{cleanText(headlineOpportunity.benchmark)}</Text>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </Layout.Section>

          {/* ===== SECTION 3: RETENTION FUNNEL ===== */}
          <Layout.Section>
            <Card padding="400">
              <Text variant="headingMd" as="h3" fontWeight="semibold">{cleanText("Retention Funnel")}</Text>
              <Text variant="bodySm" tone="subdued">{cleanText("How customers progress through loyalty stages")}</Text>
              
              <div style={{ marginTop: 20 }}>
                {retentionFunnel.map((stage, idx) => {
                  const barWidth = Math.max(5, stage.pct);
                  const isLeak = idx > 0 && retentionFunnel[idx - 1].count > stage.count;
                  const leakCount = idx > 0 ? retentionFunnel[idx - 1].count - stage.count : 0;
                  
                  return (
                    <div key={stage.label} style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Text variant="bodyMd" fontWeight="medium">{cleanText(stage.label)}</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Text variant="bodyMd" fontWeight="bold">{fmtInt(stage.count)}</Text>
                          {isLeak && leakCount > 0 && (
                            <Text variant="bodySm" tone="critical">{cleanText(`-${fmtInt(leakCount)} lost`)}</Text>
                          )}
                        </div>
                      </div>
                      <div style={{
                        height: 24,
                        background: '#f1f5f9',
                        borderRadius: 6,
                        overflow: 'hidden',
                        position: 'relative'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${barWidth}%`,
                          background: idx === 0 ? '#0891b2' : 
                                     idx === 1 ? '#06b6d4' : 
                                     idx === 2 ? '#22d3d3' : '#34d399',
                          borderRadius: 6,
                          transition: 'width 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          paddingLeft: 8
                        }}>
                          {stage.pct >= 15 && (
                            <Text variant="bodySm" fontWeight="medium">
                              <span style={{ color: 'white' }}>{fmtPct(stage.pct)}</span>
                            </Text>
                          )}
                        </div>
                        {stage.pct < 15 && (
                          <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
                            <Text variant="bodySm" tone="subdued">{fmtPct(stage.pct)}</Text>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </Layout.Section>

          {/* ===== SECTION 4: AI INSIGHTS ===== */}
          <Layout.Section>
            <Card padding="400">
              <Text variant="headingMd" as="h3" fontWeight="semibold">{cleanText("Retention Strategy")}</Text>
              <Text variant="bodySm" tone="subdued">{cleanText("AI-powered recommendations based on your customer data")}</Text>
              
              {insightsLoading ? (
                <div style={{ marginTop: 16 }}>
                  <SkeletonBodyText lines={4} />
                </div>
              ) : parsedInsights && (parsedInsights.whatWeFound.length > 0 || parsedInsights.recommendations.length > 0) ? (
                <div style={{ marginTop: 16 }}>
                  {/* What We Found */}
                  {parsedInsights.whatWeFound.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <Text variant="bodyMd" fontWeight="semibold" tone="subdued">{cleanText("What We Found")}</Text>
                      <List type="bullet">
                        {parsedInsights.whatWeFound.slice(0, 4).map((item, idx) => (
                          <List.Item key={idx}>{cleanText(item)}</List.Item>
                        ))}
                      </List>
                    </div>
                  )}
                  
                  {/* Why It Matters */}
                  {parsedInsights.whyItMatters && (
                    <div style={{ marginBottom: 16 }}>
                      <Text variant="bodyMd" fontWeight="semibold" tone="subdued">{cleanText("Why It Matters")}</Text>
                      <Text variant="bodyMd">{cleanText(parsedInsights.whyItMatters)}</Text>
                    </div>
                  )}
                  
                  {/* Recommendations */}
                  {parsedInsights.recommendations.length > 0 && (
                    <div style={{ 
                      padding: 16, 
                      background: '#f0fdfa', 
                      borderRadius: 8,
                      border: '1px solid #99f6e4'
                    }}>
                      <Text variant="bodyMd" fontWeight="semibold">{cleanText("Recommended Actions")}</Text>
                      <List type="number">
                        {parsedInsights.recommendations.slice(0, 4).map((item, idx) => (
                          <List.Item key={idx}>{cleanText(item)}</List.Item>
                        ))}
                      </List>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 16, padding: 20, background: '#f8fafc', borderRadius: 8, textAlign: 'center' }}>
                  <Text variant="bodyMd" tone="subdued">
                    {cleanText("Need more customer data to generate retention recommendations. Insights will appear once you have 10+ customers.")}
                  </Text>
                </div>
              )}
            </Card>
          </Layout.Section>

          {/* ===== SECTION 5: DETAILED METRICS (Collapsible) ===== */}
          <Layout.Section>
            <Card padding="400">
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setDetailsOpen(!detailsOpen)}
              >
                <Text variant="headingMd" as="h3" fontWeight="semibold">{cleanText("Detailed Metrics")}</Text>
                <Button 
                  variant="plain"
                  icon={detailsOpen ? ChevronUpIcon : ChevronDownIcon}
                  onClick={() => setDetailsOpen(!detailsOpen)}
                >
                  {cleanText(detailsOpen ? 'Hide' : 'Show')}
                </Button>
              </div>
              
              <Collapsible open={detailsOpen} id="detailed-metrics">
                <div style={{ marginTop: 16 }}>
                  {/* Value Distribution */}
                  {valueDistribution.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <Text variant="bodyMd" fontWeight="semibold">{cleanText("Customer Value Segments")}</Text>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                        gap: 12, 
                        marginTop: 8 
                      }}>
                        {valueDistribution.map((seg) => (
                          <div 
                            key={seg.segment}
                            style={{
                              padding: 12,
                              background: '#f8fafc',
                              borderRadius: 8,
                              textAlign: 'center'
                            }}
                          >
                            <Text variant="headingMd" fontWeight="bold">{fmtInt(seg.count)}</Text>
                            <Text variant="bodySm" fontWeight="medium">{cleanText(seg.segment)}</Text>
                            <Text variant="bodySm" tone="subdued">{cleanText(`Avg: ${fmtMoney(seg.avg_spent)}`)}</Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Frequency Distribution */}
                  {frequencyDistribution.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <Text variant="bodyMd" fontWeight="semibold">{cleanText("Purchase Frequency")}</Text>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                        gap: 12, 
                        marginTop: 8 
                      }}>
                        {frequencyDistribution.map((bucket) => (
                          <div 
                            key={bucket.bucket}
                            style={{
                              padding: 12,
                              background: '#f8fafc',
                              borderRadius: 8,
                              textAlign: 'center'
                            }}
                          >
                            <Text variant="headingMd" fontWeight="bold">{fmtInt(bucket.count)}</Text>
                            <Text variant="bodySm" fontWeight="medium">{cleanText(bucket.bucket)}</Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Additional metrics not shown elsewhere */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
                    gap: 8
                  }}>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                      <Text variant="bodySm" tone="subdued">{cleanText("Active (last 30 days)")}</Text>
                      <Text variant="headingMd" fontWeight="bold">{fmtInt(kpis.recent_customers)}</Text>
                    </div>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                      <Text variant="bodySm" tone="subdued">{cleanText("Avg Order Value")}</Text>
                      <Text variant="headingMd" fontWeight="bold">{fmtMoney(kpis.avg_order_value)}</Text>
                    </div>
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                      <Text variant="bodySm" tone="subdued">{cleanText("Health Score")}</Text>
                      <Text variant="headingMd" fontWeight="bold">{fmtPct(kpis.customer_base_health_pct)}</Text>
                    </div>
                  </div>
                </div>
              </Collapsible>
            </Card>
          </Layout.Section>

        </Layout>
      </Page>
    </LayoutWrapper>
  );
}